'use client'

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertCircle } from 'lucide-react'

import { Button, Skeleton } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

import { useSharedTRPC } from '../../../../context'

interface LeaderboardMetricsProps {
  ids: {
    courseId?: string
    challengeId?: string
    learnEnrollmentId?: string
  }
}

export function LeaderboardMetrics({ ids }: LeaderboardMetricsProps) {
  const trpc = useSharedTRPC()

  const input = useMemo(
    () => ({
      courseId: ids.courseId,
      challengeId: ids.challengeId,
      learnEnrollmentId: ids.learnEnrollmentId,
    }),
    [ids],
  )

  const enabled = !!(ids.courseId ?? ids.challengeId ?? ids.learnEnrollmentId)

  const {
    data: metrics,
    isPending,
    isError,
    refetch,
  } = useQuery({
    ...trpc.leaderboard.getMetrics.queryOptions(input),
    enabled,
    // Computed live on every read; a minute of cache keeps re-renders off the database.
    staleTime: 60_000,
  })

  if (isError) {
    return (
      <div className='srd:flex srd:items-center srd:gap-2'>
        <AlertCircle className='srd:size-4 srd:shrink-0 srd:text-error' />
        <span className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-neutral-white')}>
          Gagal memuat metrik
        </span>
        <Button size='sm' variant='outline' onClick={() => refetch()} className='srd:ml-2 srd:text-neutral-white'>
          Coba lagi
        </Button>
      </div>
    )
  }

  if (isPending) {
    return (
      <div className='srd:flex srd:flex-col srd:gap-1'>
        <Skeleton className='srd:h-6 srd:w-36 srd:rounded srd:bg-neutral-white/20' />
        <Skeleton className='srd:h-4 srd:w-44 srd:rounded srd:bg-neutral-white/20' />
      </div>
    )
  }

  const participationLabel = new Intl.NumberFormat('id-ID', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    style: 'percent',
  }).format(metrics.participationRate / 100)

  return (
    <div className='srd:flex srd:flex-col srd:gap-0.5'>
      <span className={cn(typographyVariants({ variant: 'title-lg' }), 'srd:text-neutral-white srd:font-bold')}>
        {metrics.totalParticipants.toLocaleString('id-ID')} total peserta
      </span>
      <span className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:text-grey-100')}>
        {participationLabel} rata-rata selesai
      </span>
    </div>
  )
}
